import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listMcpConnections, revokeMcpConnection, McpConnection } from "../api/McpConnectionApi";

export const McpConnectionsPanel = () => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();
  const [connections, setConnections] = useState<McpConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listMcpConnections(projectId);
      setConnections(rows);
    } catch (e: any) {
      setError(e?.message || t("Failed to load MCP connections"));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onRevoke = async (id: string) => {
    if (!projectId) return;
    if (!window.confirm(t("Revoke this MCP connection? External clients will lose access."))) {
      return;
    }
    setRevokingId(id);
    try {
      await revokeMcpConnection(projectId, id);
      await load();
    } catch (e: any) {
      setError(e?.message || t("Failed to revoke connection"));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="mt-10 border-t border-gray-200 pt-8">
      <h2 className="text-lg font-semibold text-gray-900">{t("MCP connections")}</h2>
      <p className="mt-1 text-sm text-gray-500">
        {t("OAuth installs from Cursor, Claude, and other MCP hosts. Revoke to cut off access immediately.")}
      </p>
      {loading && <p className="mt-4 text-sm text-gray-500">{t("Loading…")}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {!loading && connections.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">{t("No active MCP connections.")}</p>
      )}
      {!loading && connections.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 border border-gray-200 rounded-md">
          {connections.map((c) => (
            <li key={c.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <div className="font-medium text-gray-900">{c.clientName}</div>
                <div className="mt-1 text-xs text-gray-500">
                  {t("Scopes")}: {(c.scopes || []).join(", ") || "—"}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  {t("Created")}: {c.createdAt ? new Date(c.createdAt).toLocaleString() : "—"}
                  {c.lastUsedAt
                    ? ` · ${t("Last used")}: ${new Date(c.lastUsedAt).toLocaleString()}`
                    : ""}
                </div>
              </div>
              <button
                type="button"
                disabled={revokingId === c.id}
                onClick={() => onRevoke(c.id)}
                className="rounded-md border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {revokingId === c.id ? t("Revoking…") : t("Revoke")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default McpConnectionsPanel;
